import { useState } from "react";
import { ArrowUpRight, Check, Sparkles, Store, UserRound, Users } from "lucide-react";
import { WhatsAppIcon } from "@/components/admin_dashboard/BrandIcons";
import PricingCalculator from "./PricingCalculator";
import { additionalAddOns, plans, type LimitIcon } from "./pricingData";
import "./landing-page.css";

const LIMIT_ICONS: Record<LimitIcon, typeof Users> = { storage: UserRound, credits: Sparkles, branches: Store, staff: Users };

const FOUNDER_URL = "https://wa.me/6587169250";

export default function PricingPlans() {
  const [billing, setBilling] = useState<"monthly" | "yearly">("monthly");

  return (
    <main id="main-content" className="dibs-home">
      <section id="pricing" className="dh-section dh-pricing dh-pricing-page" aria-labelledby="dh-pricing-title">
        <div className="dh-container">
          <div className="dh-pricing-head">
          <div className="dh-section-heading">
            <div className="dh-kicker">Pricing</div>
            <h1 id="dh-pricing-title">Choose your starting point.</h1>
            <p>Choose a plan based on your needs, from working independently to scaling your team.</p>
          </div>
          <div className="dh-billing-toggle" role="group" aria-label="Billing period">
            {([["monthly", "Monthly", null], ["yearly", "Yearly", "-20%"]] as const).map(([key, label, save]) => (
              <button key={key} type="button" aria-pressed={billing === key} className={billing === key ? "is-active bg-primary" : ""} onClick={() => setBilling(key)}>
                {label}{save && <span className="dh-save-pill">{save}</span>}
              </button>
            ))}
          </div>
          </div>
          <div className="dh-plan-grid">
            {plans.map((plan) => {
              const price = plan.prices ? plan.prices[billing] : null;
              return (
                <article key={plan.name} className={`dh-plan ${plan.featured ? "is-featured" : ""}`}>
                  {plan.featured && <div className="dh-plan-badge">Most popular</div>}
                  <h3>{plan.name}</h3><p className="dh-plan-audience">{plan.audience}</p>
                  <div className="dh-price">{price === null ? <strong>Let's talk</strong> : <><span>S$</span><strong>{price}</strong><small>/ month</small></>}</div>
                  <p className="dh-price-note">{price === null ? "Pricing built around your organisation" : billing === "yearly" ? "Billed yearly" : "Billed monthly"}</p>
                  <a href={FOUNDER_URL} target="_blank" rel="noopener noreferrer">Book a demo <ArrowUpRight aria-hidden /></a>
                  <ul className="dh-plan-limits">{plan.limits.map(({ icon, text }) => { const Icon = LIMIT_ICONS[icon]; return <li key={text}><Icon aria-hidden /><span>{text}{icon === "credits" && plan.prices && <a className="dh-plan-topup" href="#addons">Need more?</a>}</span></li>; })}</ul>
                  {plan.features.length > 0 && <ul>{plan.features.map((feature) => {
                    const inherited = feature.match(/^Everything in (\w+), plus$/);
                    return inherited
                      ? <li key={feature} className="dh-plan-inherits">Everything in <strong>{inherited[1]}</strong>, plus</li>
                      : <li key={feature}><Check aria-hidden /> {feature}</li>;
                  })}</ul>}
                </article>
              );
            })}
          </div>
          <div id="addons" className="dh-addons">
            <h2 className="dh-addons-title">Additional add-ons</h2>
            <div className="dh-addons-grid">
              {additionalAddOns.map(({ name, price, unit, description, logo, icon, note }) => (
                <article key={name} className="dh-addon">
                  <div className="dh-addon-head">
                    {icon === "credits" ? <Sparkles className="dh-addon-logo text-primary" aria-hidden /> : logo ? <img className="dh-addon-logo" src={logo} alt="" /> : <WhatsAppIcon className="dh-addon-logo" size={36} />}
                    <h3>{name}</h3>
                  </div>
                  <div className="dh-price"><span>S$</span><strong>{price}</strong><small>/ {unit ?? "month"}</small></div>
                  {description && <p>{description}</p>}
                  {note && <p className="dh-addon-note">{note}</p>}
                </article>
              ))}
            </div>
          </div>
          <PricingCalculator billing={billing} demoUrl={FOUNDER_URL} />
        </div>
      </section>
    </main>
  );
}
